import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { App as NativeApp } from '@capacitor/app';
import { ClipboardList, CalendarCheck, UserRound, LayoutGrid, Wallet, Home } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { allowedModules } from '../utils/navigation';
import { isNativeApp, nativeDeepLinkPath } from './platform';
import { openExternal } from './device';
import { cleanExportCache } from './exports';
import useMobileViewport from './useMobileViewport';
import './mobile.css';

const tabIcons = { '/my-tasks': ClipboardList, '/attendance': CalendarCheck, '/my-portal': UserRound, '/finance': Wallet };
export default function MobileShell() {
  const { user, role, permissions } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const native = isNativeApp();
  const navigationType = useNavigationType();
  const keyboardOpen = useMobileViewport(native);
  const [online, setOnline] = useState(navigator.onLine);
  const modules = allowedModules(role, permissions);
  const home = modules[0]?.path || '/';
  const priority = ['/my-tasks', '/attendance', '/my-portal', '/finance'];
  const tabs = [...modules.filter(item => priority.includes(item.path)).sort((a, b) => priority.indexOf(a.path) - priority.indexOf(b.path)), ...modules.filter(item => !priority.includes(item.path))].slice(0, 3);
  const showTabs = native && user && modules.some(item => item.path === location.pathname || (item.path !== '/' && location.pathname.startsWith(item.path + '/')));

  // Router navigation functions can change identity on every route. Native listeners
  // stay mounted once and read the current navigation state through this ref.
  const navigation = useRef();
  navigation.current = { navigate, home, pathname: location.pathname, user };

  useEffect(() => {
    if (native && navigationType !== 'POP') window.scrollTo({ top: 0, behavior: 'instant' });
  }, [native, location.pathname, navigationType]);

  useEffect(() => {
    if (!native) return;
    document.documentElement.classList.add('erp-native');
    cleanExportCache();
    const offline = () => setOnline(false);
    const reconnect = () => setOnline(true);
    const links = event => {
      const anchor = event.target?.closest?.('a[href]');
      if (!anchor || event.defaultPrevented || anchor.download) return;
      const url = new URL(anchor.href);
      if (['https:', 'http:'].includes(url.protocol) && url.origin !== window.location.origin) {
        event.preventDefault(); openExternal(url.href);
      }
    };
    window.addEventListener('offline', offline); window.addEventListener('online', reconnect);
    document.addEventListener('click', links);
    let disposed = false;
    const handles = [];
    const keep = promise => promise.then(handle => disposed ? handle.remove() : handles.push(handle)).catch(() => {});
    keep(NativeApp.addListener('appUrlOpen', ({ url }) => { if (disposed) return; const path = nativeDeepLinkPath(url); if (path) navigation.current.navigate(path); }));
    NativeApp.getLaunchUrl().then(result => { if (!disposed && result?.url) { const path = nativeDeepLinkPath(result.url); if (path) navigation.current.navigate(path); } }).catch(() => {});
    return () => {
      disposed = true; handles.forEach(handle => handle.remove());
      document.documentElement.classList.remove('erp-native');
      window.removeEventListener('offline', offline); window.removeEventListener('online', reconnect);
      document.removeEventListener('click', links);
    };
  }, [native]);

  useEffect(() => {
    if (!native) return;
    document.documentElement.classList.toggle('erp-native-tabs', !!showTabs && !keyboardOpen);
    return () => document.documentElement.classList.remove('erp-native-tabs');
  }, [native, showTabs, keyboardOpen]);

  useEffect(() => {
    if (!native) return;
    let disposed = false, listener;
    NativeApp.addListener('backButton', () => {
      if (disposed) return;
      const back = new Event('erp:back', { cancelable: true });
      window.dispatchEvent(back);
      if (back.defaultPrevented) return;
      const { navigate, home, pathname, user } = navigation.current;
      if (window.history.state?.idx > 0) navigate(-1);
      else if (user && pathname !== home && pathname !== '/login') navigate(home, { replace: true });
      else NativeApp.minimizeApp().catch(() => {});
    }).then(handle => { if (disposed) handle.remove(); else listener = handle; }).catch(() => {});
    return () => { disposed = true; listener?.remove(); };
  }, [native]);

  if (!native) return null;
  return <>
    {!online && <div className="erp-mobile-offline" role="status">You’re offline. Reconnect before saving changes.</div>}
    {showTabs && !keyboardOpen && <nav className="erp-mobile-tabs" aria-label="Quick navigation">
      {tabs.map(item => { const Icon = tabIcons[item.path] || Home; return <NavLink key={item.path} to={item.path} end><Icon size={21}/><span>{item.label}</span></NavLink>; })}
      <button onClick={() => window.dispatchEvent(new Event('erp:open-modules'))}><LayoutGrid size={21}/><span>Modules</span></button>
    </nav>}
  </>;
}
